import * as React from "react";

import SvgProductImageColorBlob from "@svg/products/product-image-color-blob";

import { useIsMobile } from "@replo/design-system/hooks/use-mobile";
import twMerge from "@replo/design-system/utils/twMerge";
import { useProductFormEdit } from "@v2/features/products/hooks/useProductFormEdit";

function ProductMediaCard({
  image,
  content: { title, description, price, compareAtPrice },
  isMobile,
}: {
  image: string;
  content: {
    title: string;
    description: string;
    price: string;
    compareAtPrice?: string;
  };
  isMobile: boolean;
}) {
  return (
    <div
      className={twMerge(
        "flex flex-col items-center p-0 rounded-lg overflow-hidden",
        isMobile ? "w-full h-auto" : "w-[275px] h-[348px]",
      )}
    >
      <div
        className={twMerge(
          "flex flex-col justify-center items-center p-0 gap-2.5 bg-sheet flex-none order-0 self-stretch",
          isMobile ? "w-full aspect-[275/200]" : "w-[275px] h-[200px]",
        )}
      >
        <div
          className={twMerge(
            "flex-none order-0 self-stretch flex-grow relative",
            isMobile ? "w-full aspect-[275/200]" : "w-[275px] h-[200px]",
          )}
        >
          <img
            src={image}
            alt="Product"
            className={twMerge(
              "absolute left-0 top-0 object-cover",
              isMobile ? "w-full h-full" : "w-[275px] h-[200px]",
            )}
          />
        </div>
      </div>

      <div
        className={twMerge(
          "flex flex-col items-start p-6 gap-4 bg-white flex-none order-1 self-stretch",
          isMobile ? "w-full" : "w-[275px] h-[148px]",
        )}
      >
        <div className="flex flex-col items-start p-0 gap-3 w-full flex-none order-0 self-stretch">
          <div className="flex flex-col items-start p-0 gap-2 w-full flex-none order-0 self-stretch">
            {title ? (
              <div className="header-h5 text-default line-clamp-1">{title}</div>
            ) : (
              <div className="w-[138px] h-6 bg-sheet rounded-xl flex-none order-0" />
            )}

            {description ? (
              <div
                className="text-xs text-muted line-clamp-2 self-stretch"
                dangerouslySetInnerHTML={{ __html: description }}
              />
            ) : (
              <div className="flex flex-col gap-1 self-stretch">
                <div className="w-full h-3 bg-sheet rounded-xl flex-none order-1 self-stretch" />
                <div className="w-[198px] h-3 bg-sheet rounded-xl flex-none order-2" />
              </div>
            )}
          </div>

          {/* Price Section */}
          <div className="flex items-center gap-2">
            {price ? (
              <div className="header-h5 text-default">{price}</div>
            ) : (
              <div className="w-[61px] h-6 bg-sheet rounded-xl flex-none order-1" />
            )}

            {compareAtPrice && (
              <div className="text-xs text-muted line-through">
                {compareAtPrice}
              </div>
            )}
          </div>
        </div>
      </div>
    </div>
  );
}

export function ProductImageCard() {
  const { watch } = useProductFormEdit();
  const formData = watch();
  const isMobile = useIsMobile();

  // Get the first image from the media array (filter for images only)
  const firstImageMedia = formData.media?.find(
    (item) => item.type === "image" && item.url,
  );
  const firstImage =
    firstImageMedia?.url ?? "/images/products/replo-placeholder-two.png";
  const formattedPrice = formData.price ? `$${formData.price.toFixed(2)}` : "";
  const formattedCompareAtPrice = formData.compareAtPrice
    ? `$${formData.compareAtPrice.toFixed(2)}`
    : undefined;

  return (
    <div
      className={twMerge(
        "bg-black border border-border rounded-xl flex-none overflow-hidden relative",
        isMobile ? "w-full" : "w-[400px] h-[453px]",
      )}
      style={{
        boxShadow: "0px 4px 20px 4px rgba(0, 0, 0, 0.02)",
        order: 0,
        flexGrow: 0,
      }}
    >
      {/* Background Color Blob */}
      <div className="absolute inset-0 flex items-end justify-end">
        <SvgProductImageColorBlob blurAmount={5} />
      </div>

      {/* Product Card */}
      <div
        className={twMerge(
          "relative z-10 flex items-center justify-center w-full",
          isMobile ? "p-4" : "h-full",
        )}
      >
        <ProductMediaCard
          image={firstImage}
          content={{
            title: formData.name ?? "",
            description: formData.description ?? "",
            price: formattedPrice,
            compareAtPrice: formattedCompareAtPrice,
          }}
          isMobile={isMobile}
        />
      </div>
    </div>
  );
}
